import React, { useMemo } from "react";
import {
    Box,
    Container,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Heading,
    Card,
    CardHeader,
    CardBody,
    Button,
    Skeleton,
    useToast,
    Badge,
    HStack,
    IconButton,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Tooltip,
    VStack,
    Progress,
    Divider,
} from "@chakra-ui/react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { ViewIcon, DeleteIcon, AddIcon, ArrowUpIcon, ArrowDownIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FiDollarSign, FiCheckCircle, FiClock, FiCalendar, FiSend, FiFileText, FiTrendingUp, FiTarget } from "react-icons/fi";
import { getColor } from "../../brandConfig";

const GET_ALL_BILLS = gql`
  query GetAllBills {
    bills {
      id
      status
      isPaid
      issuedBy
      currency
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      createdAt
      updatedAt
      tenantId
    }
  }
`;

const GET_CLIENT_BY_BILL = gql`
  query GetClientDetailsByBillId($billId: ID!) {
    getClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

const DELETE_BILL = gql`
  mutation DeleteBill($id: ID!) {
    deleteBill(id: $id)
  }
`;

interface LineItem {
    description: string;
    amount: number;
}

interface Bill {
    id: string;
    status: "DRAFT" | "SENT";
    isPaid: boolean;
    issuedBy?: string;
    currency?: "AUD" | "USD";
    lineItems: {
        id: string;
        description: string;
        amount: number;
        createdAt: string;
        updatedAt: string;
    }[];
    createdAt: string;
    updatedAt: string;
    tenantId: string;
}

interface GetBillsResponse {
    bills: Bill[];
}

interface ClientInfo {
    id: string;
    fName: string;
    lName: string;
    email: string;
    phoneNumber: string;
    businessName: string;
}

const StatCard = ({
    title,
    stat,
    icon,
    color,
    helpText,
    trend,
    progress,
    goal
}: {
    title: string;
    stat: number | string;
    icon: React.ReactNode;
    color: string;
    helpText?: string;
    trend?: { value: number; isPositive: boolean };
    progress?: number;
    goal?: string;
}) => {
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textPrimary = getColor("text.primaryDark");
    const textMuted = getColor("text.mutedDark");
    
    return (
        <Box
            p={6}
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            borderRadius="lg"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px"
            borderColor={cardBorder}
            position="relative"
        >
            <VStack align="stretch" spacing={3}>
                <HStack spacing={4}>
                    <Box color={color}>
                        {icon}
                    </Box>
                    <Stat flex={1}>
                        <StatLabel color={textMuted} fontSize="sm">{title}</StatLabel>
                        <HStack align="baseline" spacing={2}>
                            <StatNumber fontSize="2xl" fontWeight="bold" color={textPrimary}>
                                {stat}
                            </StatNumber>
                            {trend && (
                                <HStack spacing={1} color={trend.isPositive ? "green.400" : "red.400"}>
                                    {trend.isPositive ? <ArrowUpIcon boxSize={3} /> : <ArrowDownIcon boxSize={3} />}
                                    <Text fontSize="sm" fontWeight="medium">
                                        {Math.abs(trend.value)}%
                                    </Text>
                                </HStack>
                            )}
                        </HStack>
                        {helpText && (
                            <StatHelpText color={textMuted} fontSize="xs">
                                {helpText}
                            </StatHelpText>
                        )}
                    </Stat>
                </HStack>
                {progress !== undefined && (
                    <Box>
                        <Progress 
                            value={progress} 
                            size="sm" 
                            colorScheme={progress >= 100 ? "green" : progress >= 50 ? "yellow" : "red"}
                            borderRadius="full"
                        />
                        {goal && (
                            <Text fontSize="xs" color={textMuted} mt={1}>
                                Goal: {goal}
                            </Text>
                        )}
                    </Box>
                )}
            </VStack>
        </Box>
    );
};

// Component to fetch and display client info for each bill
const ClientCell = ({ billId }: { billId: string }) => {
    const { loading, error, data } = useQuery(GET_CLIENT_BY_BILL, {
        variables: { billId },
        skip: !billId,
    });

    const textPrimary = getColor("text.primaryDark");
    const textMuted = getColor("text.mutedDark");
    
    if (loading) return <Skeleton height="20px" width="120px" />;
    if (error || !data?.getClientDetailsByBillId) return <Text color={textMuted}>-</Text>;

    const client = data.getClientDetailsByBillId;
    return <Text color={textPrimary}>{client.fName} {client.lName}</Text>;
};

const Bills = () => {
    const navigate = useNavigate();
    const { loading, error, data, refetch } = useQuery<GetBillsResponse>(GET_ALL_BILLS);
    const [deleteBill] = useMutation(DELETE_BILL);
    const toast = useToast();
    
    // Consistent styling from brandConfig
    const bg = getColor("background.main");
    const cardGradientBg = getColor("background.cardGradient");
    const cardBorder = getColor("border.darkCard");
    const textPrimary = getColor("text.primaryDark");
    const textSecondary = getColor("text.secondaryDark");
    const textMuted = getColor("text.mutedDark");

    React.useEffect(() => {
        if (data) {
            console.log("GET_ALL_BILLS response:", data);
        }
    }, [data]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // Calculate metrics with time-based filtering
    const billMetrics = useMemo(() => {
        if (!data?.bills) return {
            last7Days: { draft: 0, sent: 0, paid: 0, draftValue: 0, sentValue: 0, paidValue: 0 },
            last30Days: { draft: 0, sent: 0, paid: 0, draftValue: 0, sentValue: 0, paidValue: 0 },
            allTime: { draft: 0, sent: 0, paid: 0, draftValue: 0, sentValue: 0, paidValue: 0, unpaidValue: 0 },
            trends: { draft: 0, sent: 0, paid: 0, revenue: 0 }
        };

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

        const calculateMetrics = (bills: Bill[]) => {
            return bills.reduce((acc, bill) => {
                const total = bill.lineItems.reduce((sum, item) => sum + item.amount, 0);
                
                if (bill.status === "DRAFT") {
                    acc.draft++;
                    acc.draftValue += total;
                } else if (bill.status === "SENT") {
                    acc.sent++;
                    acc.sentValue += total;
                    if (bill.isPaid) {
                        acc.paid++;
                        acc.paidValue += total;
                    } else {
                        acc.unpaidValue = (acc.unpaidValue || 0) + total;
                    }
                }
                
                return acc;
            }, { draft: 0, sent: 0, paid: 0, draftValue: 0, sentValue: 0, paidValue: 0, unpaidValue: 0 });
        };

        // Last 7 days
        const last7DaysBills = data.bills.filter(bill => 
            new Date(bill.createdAt) >= sevenDaysAgo
        );
        const last7Days = calculateMetrics(last7DaysBills);

        // Previous 7 days (for trend calculation)
        const prev7DaysBills = data.bills.filter(bill => {
            const billDate = new Date(bill.createdAt);
            return billDate >= fourteenDaysAgo && billDate < sevenDaysAgo;
        });
        const prev7Days = calculateMetrics(prev7DaysBills);

        // Last 30 days
        const last30DaysBills = data.bills.filter(bill => 
            new Date(bill.createdAt) >= thirtyDaysAgo
        );
        const last30Days = calculateMetrics(last30DaysBills);

        // All time
        const allTime = calculateMetrics(data.bills);

        // Calculate trends (% change from previous period)
        const calculateTrend = (current: number, previous: number) => {
            if (previous === 0) return current > 0 ? 100 : 0;
            return Math.round(((current - previous) / previous) * 100);
        };

        const trends = {
            draft: calculateTrend(last7Days.draft, prev7Days.draft),
            sent: calculateTrend(last7Days.sent, prev7Days.sent),
            paid: calculateTrend(last7Days.paid, prev7Days.paid),
            revenue: calculateTrend(last7Days.paidValue, prev7Days.paidValue)
        };

        return { last7Days, last30Days, allTime, trends };
    }, [data]);

    // Weekly goal tracking
    const weeklyGoals = {
        proposals: 2, // At least 2 draft bills (proposals) per week
        sent: 2,      // At least 2 bills sent per week
        revenue: 5000 // Target revenue
    };

    const goalProgress = {
        proposals: Math.min(100, (billMetrics.last7Days.draft / weeklyGoals.proposals) * 100),
        sent: Math.min(100, (billMetrics.last7Days.sent / weeklyGoals.sent) * 100),
        revenue: Math.min(100, (billMetrics.last7Days.paidValue / weeklyGoals.revenue) * 100)
    };

    const handleViewBill = (bill: Bill) => {
        window.open(`/bill/${bill.id}`, '_blank');
    };

    const handleDeleteBill = async (billId: string) => {
        if (window.confirm("Are you sure you want to delete this bill?")) {
            try {
                await deleteBill({
                    variables: { id: billId }
                });
                toast({
                    title: "Bill deleted",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                refetch(); // Refresh the bills list
            } catch (error) {
                toast({
                    title: "Error deleting bill",
                    description: error instanceof Error ? error.message : "Unknown error occurred",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
            }
        }
    };

    if (error) {
        toast({
            title: "Error loading bills",
            description: error.message,
            status: "error",
            duration: 5000,
            isClosable: true,
        });
    }

    return (
        <Box bg={bg} minHeight="100vh" display="flex" flexDirection="column">
            <NavbarWithCallToAction />
            <Container maxW="container.xl" py={8} flex="1">
                <Card
                    bg={cardGradientBg}
                    backdropFilter="blur(10px)"
                    boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
                    borderWidth="1px"
                    borderColor={cardBorder}
                    overflow="hidden"
                >
                    <CardHeader borderBottomWidth="1px" borderColor={cardBorder}>
                        <HStack justify="space-between">
                            <Heading size="lg" color={textPrimary}>💰 Bills</Heading>
                            <Button
                                bg="white"
                                color="black"
                                onClick={() => navigate("/bills/new")}
                                leftIcon={<AddIcon />}
                                _hover={{
                                    bg: "gray.100",
                                    transform: "translateY(-2px)"
                                }}
                            >
                                New Bill
                            </Button>
                        </HStack>
                    </CardHeader>

                    <Box px={6} pb={6}>
                        {/* Primary KPIs - Last 7 Days with Goals */}
                        <Text fontSize="lg" fontWeight="bold" color={textPrimary} mb={4}>
                            📊 This Week's Performance (Last 7 Days)
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
                            <StatCard
                                title="Draft Proposals"
                                stat={billMetrics.last7Days.draft}
                                icon={<FiFileText size="2.5em" />}
                                color="purple.500"
                                helpText={`$${billMetrics.last7Days.draftValue.toFixed(0)} in proposals`}
                                trend={{ value: billMetrics.trends.draft, isPositive: billMetrics.trends.draft >= 0 }}
                                progress={goalProgress.proposals}
                                goal={`${weeklyGoals.proposals} proposals/week`}
                            />
                            <StatCard
                                title="Bills Sent"
                                stat={billMetrics.last7Days.sent}
                                icon={<FiSend size="2.5em" />}
                                color="blue.500"
                                helpText={`$${billMetrics.last7Days.sentValue.toFixed(0)} sent`}
                                trend={{ value: billMetrics.trends.sent, isPositive: billMetrics.trends.sent >= 0 }}
                                progress={goalProgress.sent}
                                goal={`${weeklyGoals.sent} bills/week`}
                            />
                            <StatCard
                                title="Bills Paid"
                                stat={billMetrics.last7Days.paid}
                                icon={<FiCheckCircle size="2.5em" />}
                                color="green.500"
                                helpText={`$${billMetrics.last7Days.paidValue.toFixed(0)} collected`}
                                trend={{ value: billMetrics.trends.revenue, isPositive: billMetrics.trends.revenue >= 0 }}
                                progress={goalProgress.revenue}
                                goal={`$${weeklyGoals.revenue} revenue/week`}
                            />
                            <StatCard
                                title="Conversion Rate"
                                stat={billMetrics.last7Days.sent > 0 
                                    ? `${Math.round((billMetrics.last7Days.paid / billMetrics.last7Days.sent) * 100)}%`
                                    : "0%"
                                }
                                icon={<FiTrendingUp size="2.5em" />}
                                color="teal.500"
                                helpText={`${billMetrics.last7Days.paid} of ${billMetrics.last7Days.sent} paid`}
                            />
                        </SimpleGrid>

                        <Divider my={4} borderColor={cardBorder} />

                        {/* 30-Day Metrics */}
                        <Text fontSize="lg" fontWeight="bold" color={textPrimary} mb={4}>
                            📈 Last 30 Days
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
                            <StatCard
                                title="Monthly Proposals"
                                stat={billMetrics.last30Days.draft}
                                icon={<FiFileText size="2em" />}
                                color="purple.400"
                                helpText={`$${billMetrics.last30Days.draftValue.toFixed(0)} value`}
                            />
                            <StatCard
                                title="Monthly Sent"
                                stat={billMetrics.last30Days.sent}
                                icon={<FiSend size="2em" />}
                                color="blue.400"
                                helpText={`$${billMetrics.last30Days.sentValue.toFixed(0)} value`}
                            />
                            <StatCard
                                title="Monthly Paid"
                                stat={billMetrics.last30Days.paid}
                                icon={<FiCheckCircle size="2em" />}
                                color="green.400"
                                helpText={`$${billMetrics.last30Days.paidValue.toFixed(0)} collected`}
                            />
                            <StatCard
                                title="Avg Bill Value"
                                stat={billMetrics.last30Days.sent > 0 
                                    ? `$${Math.round(billMetrics.last30Days.sentValue / billMetrics.last30Days.sent)}`
                                    : "$0"
                                }
                                icon={<FiDollarSign size="2em" />}
                                color="cyan.400"
                                helpText="Per sent bill"
                            />
                        </SimpleGrid>

                        <Divider my={4} borderColor={cardBorder} />

                        {/* All-Time Summary */}
                        <Text fontSize="lg" fontWeight="bold" color={textPrimary} mb={4}>
                            💼 All-Time Summary
                        </Text>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                            <StatCard
                                title="Total Revenue"
                                stat={`$${billMetrics.allTime.paidValue.toFixed(0)}`}
                                icon={<FiDollarSign size="2em" />}
                                color="green.500"
                                helpText={`${billMetrics.allTime.paid} bills paid`}
                            />
                            <StatCard
                                title="Outstanding"
                                stat={`$${billMetrics.allTime.unpaidValue.toFixed(0)}`}
                                icon={<FiClock size="2em" />}
                                color="orange.500"
                                helpText={`${billMetrics.allTime.sent - billMetrics.allTime.paid} unpaid bills`}
                            />
                            <StatCard
                                title="Active Proposals"
                                stat={billMetrics.allTime.draft}
                                icon={<FiTarget size="2em" />}
                                color="purple.500"
                                helpText={`$${billMetrics.allTime.draftValue.toFixed(0)} potential`}
                            />
                            <StatCard
                                title="Total Bills"
                                stat={data?.bills?.length || 0}
                                icon={<FiCalendar size="2em" />}
                                color="indigo.500"
                                helpText="All time"
                            />
                        </SimpleGrid>

                        {/* Motivational Alert if below goals */}
                        {(goalProgress.proposals < 50 || goalProgress.sent < 50) && (
                            <Box
                                mt={6}
                                p={4}
                                bg="rgba(251, 191, 36, 0.1)"
                                borderRadius="lg"
                                border="1px solid"
                                borderColor="rgba(251, 191, 36, 0.3)"
                            >
                                <HStack spacing={3}>
                                    <Text fontSize="2xl">🎯</Text>
                                    <VStack align="start" spacing={1}>
                                        <Text color={textPrimary} fontWeight="bold">
                                            Time to boost your billing activity!
                                        </Text>
                                        <Text color={textMuted} fontSize="sm">
                                            {goalProgress.proposals < 50 && `Create ${weeklyGoals.proposals - billMetrics.last7Days.draft} more proposals to hit your weekly goal. `}
                                            {goalProgress.sent < 50 && `Send ${weeklyGoals.sent - billMetrics.last7Days.sent} more bills this week to stay on track!`}
                                        </Text>
                                    </VStack>
                                </HStack>
                            </Box>
                        )}
                    </Box>

                    <CardBody>
                        <Box overflowX="auto">
                            <Table variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th color={textSecondary}>Bill ID</Th>
                                        <Th color={textSecondary}>Client Name</Th>
                                        <Th color={textSecondary}>Status</Th>
                                        <Th color={textSecondary}>Total Amount</Th>
                                        <Th color={textSecondary}>Items</Th>
                                        <Th color={textSecondary}>Payment Status</Th>
                                        <Th color={textSecondary}>Date</Th>
                                        <Th color={textSecondary}>Actions</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {loading ? (
                                        [...Array(3)].map((_, index) => (
                                            <Tr key={`skeleton-${index}`}>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                                <Td><Skeleton height="20px" /></Td>
                                            </Tr>
                                        ))
                                    ) : data?.bills?.length ? (
                                        data.bills.map((bill: Bill) => (
                                            <Tr key={bill.id}>
                                                <Td>
                                                    <Text color={textPrimary}>{bill.id}</Text>
                                                </Td>
                                                <Td color={textPrimary}>
                                                    <ClientCell billId={bill.id} />
                                                </Td>
                                                <Td>
                                                    <Badge
                                                        bg={bill.status === "SENT" ? "rgba(59, 130, 246, 0.2)" : "rgba(107, 114, 128, 0.2)"}
                                                        color={bill.status === "SENT" ? "#3B82F6" : "#6B7280"}
                                                        border="1px solid"
                                                        borderColor={bill.status === "SENT" ? "rgba(59, 130, 246, 0.3)" : "rgba(107, 114, 128, 0.3)"}
                                                    >
                                                        {bill.status}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <Text color={textPrimary}>
                                                        ${bill.lineItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2)} {bill.currency || "AUD"}
                                                    </Text>
                                                </Td>
                                                <Td>
                                                    <Text color={textSecondary}>{bill.lineItems.length} items</Text>
                                                </Td>
                                                <Td>
                                                    <Badge
                                                        bg={bill.isPaid ? "rgba(34, 197, 94, 0.2)" : "rgba(251, 191, 36, 0.2)"}
                                                        color={bill.isPaid ? "#22C55E" : "#FBBF24"}
                                                        border="1px solid"
                                                        borderColor={bill.isPaid ? "rgba(34, 197, 94, 0.3)" : "rgba(251, 191, 36, 0.3)"}
                                                    >
                                                        {bill.isPaid ? "Paid" : "Pending"}
                                                    </Badge>
                                                </Td>
                                                <Td>
                                                    <Text color={textSecondary}>{formatDate(bill.createdAt)}</Text>
                                                </Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <Tooltip label="View bill details" hasArrow>
                                                            <IconButton
                                                                aria-label="View bill details"
                                                                icon={<ViewIcon />}
                                                                size="sm"
                                                                colorScheme="teal"
                                                                onClick={() => handleViewBill(bill)}
                                                            />
                                                        </Tooltip>
                                                        <Tooltip label="Delete bill" hasArrow>
                                                            <IconButton
                                                                aria-label="Delete bill"
                                                                icon={<DeleteIcon />}
                                                                size="sm"
                                                                colorScheme="red"
                                                                onClick={() => handleDeleteBill(bill.id)}
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))
                                    ) : (
                                        <Tr>
                                            <Td colSpan={8} textAlign="center" py={8}>
                                                <Text color={textMuted}>No bills found</Text>
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </Box>
                    </CardBody>
                </Card>
            </Container>
            <FooterWithFourColumns />
        </Box>
    );
};

export default Bills;